import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { CustomList } from "components";
import { Badge } from "ui/components/badge";
import JobNameCell from "@/components/cells/jobs/name-cell";
import { STATUSES } from "@/lib/constants";
import { formatDateForDisplay } from "@/lib/utils";
import { TypeCell } from "@/modules/work/billables/cells";
import { DisplayHours } from "components/display/hours";
import { DisplayPercentage } from "components/display/percentage";
import { useContract } from "../context";
import { EmployeeCell } from "components/cell/employee/cell";
import { HoursCell } from "@/components/hours/cell";

export default function ContractJobs() {
  const { contractId } = useParams();
  const { type: contractType } = useContract();

  const columns = useMemo(() => {
    const baseColumns = [
      {
        id: "name",
        header: "Name",
        accessorKey: "name",
        sortable: true,
        cell: JobNameCell,
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }) => {
          const status = getValue();
          const statusInfo = STATUSES[status] || {
            className: "",
            label: status,
          };
          return (
            <Badge className={statusInfo.className}>{statusInfo.label}</Badge>
          );
        },
      },
      {
        id: "coordinator",
        header: "JC",
        accessorKey: "coordinator",
        cell: EmployeeCell,
      },
    ];

    switch (contractType) {
      case "fixed":
        return [
          ...baseColumns,
          {
            id: "milestones",
            header: "Milestones",
            accessorKey: "dashboard.milestones",
            cell: ({ getValue }) => {
              const milestones = getValue() || {};
              return (
                <div>
                  {milestones.closed ?? 0} / {milestones.total ?? 0}
                </div>
              );
            },
          },
          {
            id: "allocated_hours",
            header: "Allocated Hours",
            accessorKey: "dashboard.allocated_hours",
            cell: HoursCell,
          },
          {
            id: "spent_vs_consumed",
            header: "Spent vs Consumed",
            accessorFn: (row) => row?.dashboard,
            cell: ({ getValue }) => {
              const dashboard = getValue() || {};
              return (
                <div>
                  <DisplayHours value={dashboard.spent_hours} /> /{" "}
                  <DisplayHours value={dashboard.consumed_hours} />
                </div>
              );
            },
          },
          {
            id: "effort_progress",
            header: "Effort Progress",
            accessorKey: "dashboard.effort_progress",
            cell: ({ getValue }) => (
              <DisplayPercentage value={getValue()} precision={0} withSuffix />
            ),
          },
          {
            id: "remaining_allocated_hours",
            header: "Remaining Hours",
            accessorKey: "dashboard.remaining_allocated_hours",
            cell: HoursCell,
          },
        ];

      case "dedicated":
        return [
          ...baseColumns,
          {
            id: "timeline",
            header: "Timeline (Months)",
            accessorKey: "dashboard.timeline",
            cell: ({ getValue }) => {
              const timeline = getValue() || {};
              return (
                <div>
                  {timeline.completed_months ?? 0} /{" "}
                  {timeline.total_months ?? 0}
                </div>
              );
            },
          },
          {
            id: "total_hours",
            header: "Total Hours",
            accessorKey: "dashboard.total_hours",
            cell: HoursCell,
          },
          {
            id: "spent_vs_consumed",
            header: "Spent vs Consumed",
            accessorFn: (row) => row?.dashboard,
            cell: ({ getValue }) => {
              const dashboard = getValue() || {};
              return (
                <div>
                  <DisplayHours value={dashboard.spent_hours} /> /{" "}
                  <DisplayHours value={dashboard.consumed_hours} />
                </div>
              );
            },
          },
          {
            id: "active_billable_working_hours",
            header: "Active Billable Hours",
            accessorKey: "dashboard.active_billable_working_hours",
            cell: HoursCell,
          },
        ];

      case "hourly":
        return [
          ...baseColumns,
          {
            id: "approved_vs_logged",
            header: "Approved vs Logged",
            accessorFn: (row) => row?.dashboard,
            cell: ({ getValue }) => {
              const dashboard = getValue() || {};
              return (
                <div>
                  <DisplayHours value={dashboard.approved_hours} /> /{" "}
                  <DisplayHours value={dashboard.logged_hours} />
                </div>
              );
            },
          },
          {
            id: "billable_hours",
            header: "Billable Hours",
            accessorKey: "dashboard.billable_hours",
            cell: HoursCell,
          },
          {
            id: "billed_hours",
            header: "Billed Hours",
            accessorKey: "dashboard.billed_hours",
            cell: HoursCell,
          },
          {
            id: "billing_difference",
            header: "Billing Difference",
            accessorKey: "dashboard.billing_difference",
            cell: HoursCell,
          },
          {
            id: "last_period_billable_hours",
            header: "Last Period Billable",
            accessorKey: "dashboard.last_period_billable_hours",
            cell: HoursCell,
          },
          {
            id: "last_period_billed_hours",
            header: "Last Period Billed",
            accessorKey: "dashboard.last_period_billed_hours",
            cell: HoursCell,
          },

          {
            id: "total_milestone_billable_hours",
            header: "Total Milestone Billable",
            accessorKey: "dashboard.total_milestone_billable_hours",
            cell: HoursCell,
          },
          {
            id: "milestone_config_hours_difference",
            header: "Milestone vs Config Diff",
            accessorKey: "dashboard.milestone_config_hours_difference",
            cell: HoursCell,
          },
        ];

      case "bucket":
        return [
          ...baseColumns,
          {
            id: "bucket_size",
            header: "Bucket Size",
            accessorKey: "dashboard.bucket_size",
            cell: HoursCell,
          },
          {
            id: "redeemed_hours",
            header: "Redeemed Hours",
            accessorKey: "dashboard.redeemed_hours",
            cell: HoursCell,
          },
          {
            id: "remaining_hours",
            header: "Remaining Hours",
            accessorKey: "dashboard.remaining_hours",
            cell: HoursCell,
          },
          {
            id: "billable_hours",
            header: "Billable Hours",
            accessorKey: "dashboard.billable_hours",
            cell: HoursCell,
          },
          {
            id: "hours_to_redeem_for_target_margin",
            header: "Target Redeem Hours",
            accessorKey: "dashboard.hours_to_redeem_for_target_margin",
            cell: HoursCell,
          },
          {
            id: "approved_vs_logged",
            header: "Approved vs Logged",
            accessorFn: (row) => row?.dashboard,
            cell: ({ getValue }) => {
              const dashboard = getValue() || {};
              return (
                <div>
                  <DisplayHours value={dashboard.approved_hours} /> /{" "}
                  <DisplayHours value={dashboard.logged_hours} />
                </div>
              );
            },
          },
        ];

      case "recurring":
        return [
          ...baseColumns,
          {
            id: "billing_frequency",
            header: "Frequency",
            accessorKey: "dashboard.billing_frequency",
            cell: ({ getValue }) => (
              <span className="capitalize">{getValue() || "-"}</span>
            ),
          },
          {
            id: "billing_interval",
            header: "Interval",
            accessorKey: "dashboard.billing_interval",
            cell: ({ getValue }) => getValue() ?? "-",
          },

          {
            id: "approved_vs_logged",
            header: "Approved vs Logged",
            accessorFn: (row) => row?.dashboard,
            cell: ({ getValue }) => {
              const dashboard = getValue() || {};
              return (
                <div>
                  <DisplayHours value={dashboard.approved_hours} /> /{" "}
                  <DisplayHours value={dashboard.logged_hours} />
                </div>
              );
            },
          },
        ];

      default:
        return [
          ...baseColumns,
          {
            id: "type",
            header: "Type",
            accessorKey: "type",
            cell: TypeCell,
            sortable: false,
          },
          {
            id: "start_date",
            header: "Start Date",
            accessorKey: "start_date",
            sortable: true,
            cell: (info) =>
              formatDateForDisplay(info.getValue(), { addSuffix: true }),
          },
          {
            id: "end_date",
            header: "End Date",
            accessorKey: "end_date",
            sortable: true,
            cell: (info) =>
              formatDateForDisplay(info.getValue(), { addSuffix: true }),
          },
        ];
    }
  }, [contractType]);

  const defaultFilters = {
    contractId: contractId,
  };

  return (
    <div className="flex-1 overflow-hidden">
      <CustomList
        title="Jobs"
        storageKey={`contract-jobs-${contractId}-${contractType || "all"}`}
        endPoint="revenue-calculation/contract-jobs-dashboard"
        filters={defaultFilters}
        columns={columns}
        sortBy="start_date"
        href={(item) => `/work/jobs/${item.id}`}
      />
    </div>
  );
}
